import { findParticipantByRole, isParticipantLive } from '@/lib/chat-presence';
import { cn } from '@/lib/utils';
import type { ChatPresenceParticipant } from '@/types/chat-presence';

/**
 * Compact “Visitor · online/offline” for the lead query chat header (not the full Presence strip).
 */
export function VisitorPresenceBar({
  participants,
  className,
}: {
  participants: ChatPresenceParticipant[];
  className?: string;
}) {
  const visitor = findParticipantByRole(participants, 'LEAD');
  const online = isParticipantLive(visitor);
  const lastSeen = visitor?.lastSeenAt;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border/80 bg-gradient-to-r from-muted/50 to-muted/20 px-3 py-2',
        className
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span
          className={cn(
            'h-2 w-2 shrink-0 rounded-full ring-2 ring-background',
            online ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.45)]' : 'bg-muted-foreground/40'
          )}
          aria-hidden
        />
        <span className="text-sm font-medium text-foreground">Visitor</span>
        <span className="text-muted-foreground text-sm" aria-hidden>
          ·
        </span>
        <span
          className={cn(
            'text-sm font-semibold tabular-nums',
            online ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground'
          )}
        >
          {online ? 'online' : 'offline'}
        </span>
      </div>
      {!online && lastSeen ? (
        <span className="text-xs text-muted-foreground truncate max-w-full sm:max-w-[220px]">
          Last seen {new Date(lastSeen).toLocaleString()}
        </span>
      ) : null}
    </div>
  );
}
